import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getApiError } from 'utils/lib/helpers/oystehrApi';
import { AdHocRow, LlmDatasetSchema } from 'utils/lib/types/adhoc/datasets/llm-schema';
import {
  AD_HOC_REPORT_DEFAULT_MODEL,
  AdHocReportModel,
  GenerateAdHocReportInput,
  GenerateAdHocReportOutput,
} from 'utils/lib/types/adhoc/generation/generate.types';
import { InferDatasetFeedback } from 'utils/lib/types/adhoc/generation/infer.types';
import { AdHocDateRangeFilter } from 'utils/lib/types/adhoc/query/date-range';
import { ADHOC_RUNTIME_VERSION, SavedAdHocReportDefinition } from 'utils/lib/types/adhoc/saved/saved.types';
import {
  AD_HOC_REPORT_EDIT_ROLES,
  AD_HOC_REPORT_MODEL_PICKER_ROLES,
  AD_HOC_REPORT_VIEW_ROLES,
} from 'utils/lib/types/api/adhoc-report-access';
import { inferAdHocReportLayers, listAdHocReports, saveAdHocReport } from '../../../api/api';
import { useApiClients } from '../../../hooks/useAppClients';
import useEvolveUser from '../../../hooks/useEvolveUser';
import { datasetCatalog, getDataset, otherDatasetsFor } from '../datasets/registry';
import { showAdHocDebugLog } from '../debug';
import { SANDBOX_TIMEOUT_MESSAGE } from '../hooks/useSandbox';
import { toLocalYmd } from '../query/dataset-query';
import { runAdHocReportGeneration } from '../query/generation-query';

// How many times to transparently regenerate after a runtime error before surfacing it to the user.
// 1 initial generation + up to 2 repairs.
const MAX_AUTO_RETRIES = 2;

type PreviousAttempt = NonNullable<GenerateAdHocReportInput['previousAttempt']>;

interface InferResult {
  datasetIds: string[];
  options: Record<string, boolean>;
  unavailable?: string[];
  hint?: string;
}

export interface UnavailableRequest {
  concepts: string[];
  hint?: string;
}

export interface MultiDatasetRequest {
  datasets: string[];
  hint?: string;
}

export function rangeFromControls(
  filter: AdHocDateRangeFilter,
  customDate: string,
  customStartDate: string,
  customEndDate: string
): { start: string; end: string } {
  const today = DateTime.now().setZone('America/New_York').startOf('day');
  switch (filter) {
    case 'today':
      return { start: today.toISO() ?? '', end: today.endOf('day').toISO() ?? '' };
    case 'yesterday': {
      const y = today.minus({ days: 1 });
      return { start: y.toISO() ?? '', end: y.endOf('day').toISO() ?? '' };
    }
    case 'last-7-days':
      return { start: today.minus({ days: 6 }).toISO() ?? '', end: today.endOf('day').toISO() ?? '' };
    case 'last-30-days':
      return { start: today.minus({ days: 29 }).toISO() ?? '', end: today.endOf('day').toISO() ?? '' };
    case 'custom': {
      const d = DateTime.fromISO(customDate, { zone: 'America/New_York' });
      return { start: d.startOf('day').toISO() ?? '', end: d.endOf('day').toISO() ?? '' };
    }
    case 'customRange': {
      const s = DateTime.fromISO(customStartDate, { zone: 'America/New_York' });
      const e = DateTime.fromISO(customEndDate, { zone: 'America/New_York' });
      return { start: s.startOf('day').toISO() ?? '', end: e.endOf('day').toISO() ?? '' };
    }
    default:
      return { start: today.toISO() ?? '', end: today.endOf('day').toISO() ?? '' };
  }
}

function schemaDateRange(range: { start: string; end: string }): LlmDatasetSchema['dateRange'] {
  const start = toLocalYmd(range.start);
  const end = toLocalYmd(range.end);

  return start && end ? { start, end } : undefined;
}

function toMultiDatasetRequest({ datasetIds, hint }: Pick<InferResult, 'datasetIds' | 'hint'>): MultiDatasetRequest {
  return { datasets: datasetIds.map((id) => getDataset(id)?.label ?? id), ...(hint ? { hint } : {}) };
}

function defaultOptionsFor(datasetId: string): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  (getDataset(datasetId)?.options ?? []).forEach((opt) => {
    out[opt.id] = !!opt.default;
  });
  return out;
}

type UseReportBuilder = {
  oystehrZambda: ReturnType<typeof useApiClients>['oystehrZambda'];
  canView: boolean;
  canCreate: boolean;
  canPickModel: boolean;
  model: AdHocReportModel;
  datasetId: string | null;
  dateRange: AdHocDateRangeFilter;
  customDate: string;
  customStartDate: string;
  customEndDate: string;
  rows: AdHocRow[] | null;
  schema: LlmDatasetSchema | null;
  datasetOptions: Record<string, boolean>;
  loading: boolean;
  error: string | null;
  request: string;
  generating: boolean;
  /** Layer labels being auto-loaded after the model asked for them — the shown report is incomplete meanwhile. */
  loadingLayers: string[];
  generatedCode: string | null;
  generatedTitle: string | undefined;
  generateError: string | null;
  unavailableRequest: UnavailableRequest | null;
  multiDatasetRequest: MultiDatasetRequest | null;
  renderError: string | null;
  showSchema: boolean;
  showCode: boolean;
  loadedSavedId: string | null;
  savedName: string;
  savedDescription: string;
  saveDialogOpen: boolean;
  saving: boolean;
  setDateRange: (dateRange: AdHocDateRangeFilter) => void;
  setCustomDate: (customDate: string) => void;
  setCustomStartDate: (customStartDate: string) => void;
  setCustomEndDate: (customEndDate: string) => void;
  setRequest: (request: string) => void;
  setModel: (model: AdHocReportModel) => void;
  setShowSchema: (showSchema: boolean) => void;
  setShowCode: (showCode: boolean) => void;
  setSavedName: (savedName: string) => void;
  setSavedDescription: (savedDescription: string) => void;
  setSaveDialogOpen: (saveDialogOpen: boolean) => void;
  handleFetch: () => void;
  handleGenerate: () => void;
  handleReset: () => void;
  handleRenderError: (message: string) => void;
  handleRendered: () => void;
  handleSave: (mode: 'update' | 'new') => Promise<void>;
  openSaveDialog: () => void;
};

export function useReportBuilder(): UseReportBuilder {
  const { oystehrZambda } = useApiClients();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const user = useEvolveUser();
  const canView = user?.hasRole(AD_HOC_REPORT_VIEW_ROLES) ?? false;
  const canCreate = user?.hasRole(AD_HOC_REPORT_EDIT_ROLES) ?? false;
  const canPickModel = user?.hasRole(AD_HOC_REPORT_MODEL_PICKER_ROLES) ?? false;

  const [datasetId, setDatasetId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<AdHocDateRangeFilter>('last-30-days');
  const [customDate, setCustomDate] = useState<string>(DateTime.now().toFormat('yyyy-MM-dd'));
  const [customStartDate, setCustomStartDate] = useState<string>(
    DateTime.now().minus({ days: 30 }).toFormat('yyyy-MM-dd')
  );
  const [customEndDate, setCustomEndDate] = useState<string>(DateTime.now().toFormat('yyyy-MM-dd'));
  const [datasetOptions, setDatasetOptions] = useState<Record<string, boolean>>({});

  const [rows, setRows] = useState<AdHocRow[] | null>(null);
  const [schema, setSchema] = useState<LlmDatasetSchema | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [request, setRequest] = useState('');
  const [model, setModel] = useState<AdHocReportModel>(AD_HOC_REPORT_DEFAULT_MODEL);
  const [generating, setGenerating] = useState(false);
  const [loadingLayers, setLoadingLayers] = useState<string[]>([]);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [generatedTitle, setGeneratedTitle] = useState<string | undefined>(undefined);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [unavailableRequest, setUnavailableRequest] = useState<UnavailableRequest | null>(null);
  const [multiDatasetRequest, setMultiDatasetRequest] = useState<MultiDatasetRequest | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [showSchema, setShowSchema] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const activeRequestRef = useRef('');
  const autoRetryRef = useRef(0);
  const autoFixedRef = useRef(false);

  const [loadedSavedId, setLoadedSavedId] = useState<string | null>(null);
  const [savedName, setSavedName] = useState<string>('');
  const [savedDescription, setSavedDescription] = useState<string>('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const loadAttemptedRef = useRef(false);
  const [pendingRegenerate, setPendingRegenerate] = useState<string | null>(null);

  const getDateRangeIso = useCallback(
    (filter: AdHocDateRangeFilter) => rangeFromControls(filter, customDate, customStartDate, customEndDate),
    [customDate, customStartDate, customEndDate]
  );

  const fetchWithOptions = useCallback(
    async (
      datasetId: string,
      opts: Record<string, boolean>
    ): Promise<{ rows: AdHocRow[]; schema: LlmDatasetSchema } | null> => {
      if (!oystehrZambda) return null;
      const dataset = getDataset(datasetId);
      if (!dataset) return null;
      setLoading(true);
      setError(null);
      try {
        const range = getDateRangeIso(dateRange);
        const fetched = await dataset.fetch({ oystehrZambda, queryClient, dateRange: range, options: opts });
        const builtSchema: LlmDatasetSchema = {
          ...dataset.buildSchema(fetched, opts),
          dateRange: schemaDateRange(range),
          otherDatasets: otherDatasetsFor(datasetId),
        };
        showAdHocDebugLog('fetch', `fetched ${fetched.length} rows`, {
          datasetId,
          options: opts,
          fields: builtSchema.fields.length,
        });
        setDatasetId(datasetId);
        setRows(fetched);
        setSchema(builtSchema);
        setDatasetOptions(opts);
        return { rows: fetched, schema: builtSchema };
      } catch (e) {
        showAdHocDebugLog('fetch', 'FAILED', e);
        setError(getApiError({ error: e, defaultError: 'Failed to fetch data' }));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [oystehrZambda, queryClient, dateRange, getDateRangeIso]
  );

  const handleFetch = useCallback((): void => {
    if (datasetId) void fetchWithOptions(datasetId, datasetOptions);
  }, [fetchWithOptions, datasetId, datasetOptions]);

  const inferOptions = useCallback(
    async (message: string, feedback?: InferDatasetFeedback): Promise<InferResult> => {
      if (!oystehrZambda) throw new Error('Not connected');
      const inferred = await inferAdHocReportLayers(oystehrZambda, {
        datasets: datasetCatalog(),
        request: message,
        ...(feedback ? { feedback } : {}),
      });
      showAdHocDebugLog('infer', 'classified request', { ...inferred, feedback });
      const datasetIds = inferred.datasets.map((d) => d.id);
      if (inferred.unavailable?.length) {
        return { datasetIds, options: {}, unavailable: inferred.unavailable, hint: inferred.hint };
      }

      const options = defaultOptionsFor(datasetIds[0]);

      if (datasetIds.length > 1) return { datasetIds, options, hint: inferred.hint };

      inferred.datasets[0].layerIds.forEach((layerId) => {
        if (layerId in options) options[layerId] = true;
      });

      return { datasetIds, options };
    },
    [oystehrZambda]
  );

  const requestGenerate = useCallback(
    async (
      message: string,
      useSchema: LlmDatasetSchema,
      previousAttempt?: PreviousAttempt
    ): Promise<GenerateAdHocReportOutput> => {
      if (!oystehrZambda) throw new Error('Not connected');
      showAdHocDebugLog('generate', 'requesting report code', {
        request: message,
        repair: !!previousAttempt,
        fields: useSchema.fields.map((f) => f.name),
        rowCount: useSchema.rowCount,
      });
      const result = await runAdHocReportGeneration(oystehrZambda, {
        schema: useSchema,
        request: message,
        previousAttempt,
        ...(canPickModel ? { model } : {}),
      });
      showAdHocDebugLog('generate', 'received report code', {
        title: result.title,
        needsLayers: result.needsLayers,
        needsDataset: result.needsDataset,
        codeLength: result.code.length,
      });
      return result;
    },
    [oystehrZambda, canPickModel, model]
  );

  const commitGenerated = useCallback((message: string, result: GenerateAdHocReportOutput): void => {
    activeRequestRef.current = message;
    setGeneratedCode(result.code);
    setGeneratedTitle(result.title);
  }, []);

  const orchestrateRef = useRef<(m: string, infer: boolean, prev?: PreviousAttempt) => Promise<void>>();

  const orchestrate = useCallback(
    async (message: string, infer: boolean, previousAttempt?: PreviousAttempt): Promise<void> => {
      if (!oystehrZambda) return;
      setGenerating(true);
      setGenerateError(null);
      setRenderError(null);
      setUnavailableRequest(null);
      setMultiDatasetRequest(null);
      try {
        let activeId = datasetId;
        let activeOpts = datasetOptions;
        let activeSchema = schema;
        if (infer) {
          const inferred = await inferOptions(message);
          if (inferred.unavailable?.length) {
            setUnavailableRequest({ concepts: inferred.unavailable, hint: inferred.hint });
            return;
          }
          if (inferred.datasetIds.length > 1) {
            setMultiDatasetRequest(toMultiDatasetRequest(inferred));
            return;
          }
          activeId = inferred.datasetIds[0];
          activeOpts = inferred.options;
          const fetched = await fetchWithOptions(activeId, activeOpts);
          if (!fetched) return;
          activeSchema = fetched.schema;
        } else if (!activeSchema && activeId) {
          const fetched = await fetchWithOptions(activeId, activeOpts);
          if (!fetched) return;
          activeSchema = fetched.schema;
        }

        if (!activeId || !activeSchema) return;

        let currentId = activeId;
        let repicked = false;
        let switched = false;

        const reject = (apply: () => void): null => {
          setGeneratedCode(null);
          setGeneratedTitle(undefined);
          apply();
          return null;
        };

        const resolveDataset = async (res: GenerateAdHocReportOutput): Promise<GenerateAdHocReportOutput | null> => {
          const needed = res.needsDataset;
          if (!infer || !needed) return res;

          if (switched) {
            return reject(() => setMultiDatasetRequest(toMultiDatasetRequest({ datasetIds: [currentId, needed.id] })));
          }

          if (repicked) return res;

          repicked = true;

          const retried = await inferOptions(message, {
            datasetId: currentId,
            concepts: needed.concepts,
            suggestedDatasetId: needed.id,
          }).catch((e) => {
            showAdHocDebugLog('infer', 're-pick failed — keeping the first report', e);
            return null;
          });

          if (!retried) return res;

          if (retried.unavailable?.length) {
            const { unavailable, hint } = retried;
            return reject(() => setUnavailableRequest({ concepts: unavailable, hint }));
          }

          if (retried.datasetIds.length > 1)
            return reject(() => setMultiDatasetRequest(toMultiDatasetRequest(retried)));

          if (retried.datasetIds[0] === currentId) return res;

          const refetched = await fetchWithOptions(retried.datasetIds[0], retried.options);

          if (!refetched) return res;

          currentId = retried.datasetIds[0];
          activeOpts = retried.options;
          switched = true;

          return resolveDataset(await requestGenerate(message, refetched.schema));
        };

        const result = await resolveDataset(await requestGenerate(message, activeSchema, previousAttempt));

        if (!result) return;

        commitGenerated(message, result);

        const wanted = (result.needsLayers ?? []).filter((id) => id in activeOpts && !activeOpts[id]);

        if (wanted.length) {
          const layerLabels = getDataset(currentId)?.options ?? [];
          setLoadingLayers(wanted.map((id) => layerLabels.find((l) => l.id === id)?.label ?? id));
          const merged = { ...activeOpts };
          wanted.forEach((id) => (merged[id] = true));
          const refetched = await fetchWithOptions(currentId, merged);

          if (refetched) {
            const rerun = await resolveDataset(await requestGenerate(message, refetched.schema, previousAttempt));
            if (rerun) commitGenerated(message, rerun);
          }
        }
      } catch (e) {
        showAdHocDebugLog('generate', 'orchestrate FAILED', e);
        setGenerateError(getApiError({ error: e, defaultError: 'Failed to generate report' }));
      } finally {
        setGenerating(false);
        setLoadingLayers([]);
      }
    },
    [oystehrZambda, datasetId, datasetOptions, schema, inferOptions, fetchWithOptions, requestGenerate, commitGenerated]
  );

  orchestrateRef.current = orchestrate;

  useEffect(() => {
    if (!pendingRegenerate || loading || generating) return;
    const request = pendingRegenerate;
    setPendingRegenerate(null);
    void orchestrate(request, false);
  }, [pendingRegenerate, loading, generating, orchestrate]);

  const handleGenerate = useCallback((): void => {
    if (!request.trim() || !canCreate) return;
    autoRetryRef.current = 0;
    autoFixedRef.current = false;
    setGeneratedCode(null);
    void orchestrate(request, true);
  }, [request, canCreate, orchestrate]);

  const handleRenderError = useCallback(
    (message: string): void => {
      if (
        message !== SANDBOX_TIMEOUT_MESSAGE &&
        canCreate &&
        autoRetryRef.current < MAX_AUTO_RETRIES &&
        !generating &&
        activeRequestRef.current
      ) {
        autoRetryRef.current += 1;
        autoFixedRef.current = true;
        showAdHocDebugLog('autofix', `regenerating after runtime error (attempt ${autoRetryRef.current})`, {
          message,
        });
        const failingCode = generatedCode;
        void orchestrateRef.current?.(
          activeRequestRef.current,
          false,
          failingCode ? { code: failingCode, error: message } : undefined
        );
        return;
      }
      setRenderError(message);
    },
    [canCreate, generating, generatedCode]
  );

  const handleReset = useCallback((): void => {
    setGeneratedCode(null);
    setGeneratedTitle(undefined);
    setGenerateError(null);
    setUnavailableRequest(null);
    setMultiDatasetRequest(null);
    setRenderError(null);
    setLoadedSavedId(null);
    activeRequestRef.current = '';
    autoRetryRef.current = 0;
    autoFixedRef.current = false;
  }, []);

  useEffect(() => {
    const savedId = searchParams.get('saved');

    if (!savedId || !oystehrZambda || !canView || loadAttemptedRef.current) return;

    loadAttemptedRef.current = true;

    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const { reports } = await listAdHocReports(oystehrZambda);
        const saved = reports.find((r) => r.id === savedId);
        if (!saved) {
          setError('That saved report no longer exists.');
          return;
        }
        const filter = saved.criteria.dateRange as AdHocDateRangeFilter;
        setDatasetId(saved.datasetId);
        setDateRange(filter);
        if (saved.criteria.customDate) setCustomDate(saved.criteria.customDate);
        if (saved.criteria.customStartDate) setCustomStartDate(saved.criteria.customStartDate);
        if (saved.criteria.customEndDate) setCustomEndDate(saved.criteria.customEndDate);
        setRequest(saved.request);
        setSavedDescription(saved.description ?? '');
        setLoadedSavedId(saved.id);
        setSavedName(saved.name);
        const savedOptions = saved.criteria.options ?? defaultOptionsFor(saved.datasetId);
        setDatasetOptions(savedOptions);

        const dataset = getDataset(saved.datasetId);
        if (!dataset) {
          setError(`Unknown dataset "${saved.datasetId}".`);
          return;
        }
        const range = rangeFromControls(
          filter,
          saved.criteria.customDate ?? customDate,
          saved.criteria.customStartDate ?? customStartDate,
          saved.criteria.customEndDate ?? customEndDate
        );
        const fetched = await dataset.fetch({ oystehrZambda, dateRange: range, options: savedOptions, queryClient });
        const builtSchema: LlmDatasetSchema = {
          ...dataset.buildSchema(fetched, savedOptions),
          dateRange: schemaDateRange(range),
          otherDatasets: otherDatasetsFor(saved.datasetId),
        };
        setRows(fetched);
        setSchema(builtSchema);
        activeRequestRef.current = saved.request;
        autoRetryRef.current = 0;

        if ((saved.runtimeVersion ?? 0) !== ADHOC_RUNTIME_VERSION) {
          showAdHocDebugLog('saved', 'runtime version mismatch', {
            saved: saved.runtimeVersion,
            current: ADHOC_RUNTIME_VERSION,
          });

          if (!canCreate) {
            setError('This report was built for an older report engine and needs an administrator to rebuild it.');
          } else {
            enqueueSnackbar('This report was rebuilt for an updated report engine.', {
              variant: 'info',
            });
            autoFixedRef.current = true;
            setPendingRegenerate(saved.request);
          }
        } else {
          setGeneratedCode(saved.code);
          setGeneratedTitle(saved.title);
        }
      } catch (e) {
        setError(getApiError({ error: e, defaultError: 'Failed to load saved report' }));
      } finally {
        setLoading(false);
      }
    })();
    // Must run only when the client becomes available. Re-running on searchParams changes
    // would re-load the saved report from scratch (refetch data, rebuild schema, re-set the code →
    // the frame remounts), discarding the report currently on screen. So oystehrZambda is the sole dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oystehrZambda]);

  const buildDefinition = useCallback(
    (name: string, code: string, reportDatasetId: string): SavedAdHocReportDefinition => ({
      name: name.trim(),
      description: savedDescription.trim() || undefined,
      datasetId: reportDatasetId,
      criteria: { dateRange, customDate, customStartDate, customEndDate, options: datasetOptions },
      request: activeRequestRef.current || request,
      code,
      title: generatedTitle,
      runtimeVersion: ADHOC_RUNTIME_VERSION,
    }),
    [savedDescription, dateRange, customDate, customStartDate, customEndDate, datasetOptions, request, generatedTitle]
  );

  const handleRendered = useCallback((): void => {
    setRenderError(null);

    if (!autoFixedRef.current) return;

    autoFixedRef.current = false;

    if (!canCreate || !oystehrZambda || !loadedSavedId || !generatedCode || !datasetId) return;

    void saveAdHocReport(oystehrZambda, {
      reportId: loadedSavedId,
      definition: buildDefinition(savedName || generatedTitle || 'Report', generatedCode, datasetId),
    }).catch((e) => console.warn('Could not persist auto-fixed report', e));
  }, [canCreate, oystehrZambda, loadedSavedId, generatedCode, datasetId, buildDefinition, savedName, generatedTitle]);

  const handleSave = useCallback(
    async (mode: 'update' | 'new'): Promise<void> => {
      if (!oystehrZambda || !generatedCode || !datasetId || !savedName.trim()) return;
      setSaving(true);
      try {
        const { report: saved } = await saveAdHocReport(oystehrZambda, {
          reportId: mode === 'update' ? loadedSavedId ?? undefined : undefined,
          definition: buildDefinition(savedName, generatedCode, datasetId),
        });
        setLoadedSavedId(saved.id);
        setSavedName(saved.name);
        setSaveDialogOpen(false);
        enqueueSnackbar(`Saved report “${saved.name}”.`, { variant: 'success' });
      } catch (e) {
        enqueueSnackbar(e instanceof Error ? e.message : 'Could not save the report.', { variant: 'error' });
      } finally {
        setSaving(false);
      }
    },
    [oystehrZambda, generatedCode, datasetId, savedName, loadedSavedId, buildDefinition]
  );

  const openSaveDialog = useCallback((): void => {
    setSavedName((n) => n || generatedTitle || '');
    setSaveDialogOpen(true);
  }, [generatedTitle]);

  return {
    oystehrZambda,
    canView,
    canCreate,
    canPickModel,
    model,
    datasetId,
    dateRange,
    customDate,
    customStartDate,
    customEndDate,
    rows,
    schema,
    datasetOptions,
    loading,
    error,
    request,
    generating,
    loadingLayers,
    generatedCode,
    generatedTitle,
    generateError,
    unavailableRequest,
    multiDatasetRequest,
    renderError,
    showSchema,
    showCode,
    loadedSavedId,
    savedName,
    savedDescription,
    saveDialogOpen,
    saving,
    setDateRange,
    setCustomDate,
    setCustomStartDate,
    setCustomEndDate,
    setRequest,
    setModel,
    setShowSchema,
    setShowCode,
    setSavedName,
    setSavedDescription,
    setSaveDialogOpen,
    handleFetch,
    handleGenerate,
    handleReset,
    handleRenderError,
    handleRendered,
    handleSave,
    openSaveDialog,
  };
}
